import { useBooleanFlagValue } from '@openfeature/react-sdk';

/**
 * Master gate for workflow (Routines) event-trigger authoring — mirrors the BE
 * `release-ml-scribe-enable-event-triggers` flag that gates all agent-harness
 * event triggers. Off ⇒ the whole "Run on events" section is hidden (the BE won't
 * dispatch, so there's nothing to author). Rollout/kill switch — default OFF.
 * OpenFeature/Flagsmith, so honour the local-dev force-enable escape hatch.
 */
export const useIsEventTriggersEnabled = () => {
  const enabled = useBooleanFlagValue('release-ml-scribe-enable-event-triggers', false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return enabled;
};

/**
 * Nested gate for just the "Only run when…" semantic-condition authoring field,
 * inside the event-triggers section above. Rollout/kill switch — default OFF, no
 * manual exposure; lets us roll out (or disable) semantic-condition authoring
 * independently of base event triggers. OpenFeature/Flagsmith, so honour the
 * local-dev force-enable escape hatch.
 */
export const useIsWorkflowEventRuleConditionsEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-scribe-event-rule-semantic-conditions',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return enabled;
};

/**
 * Nested gate for the "While the session is transcribing" event-type option.
 * Mirrors the BE emit kill switch `release-ml-scribe-session-transcribing-events`.
 * Off (default) ⇒ the option is hidden from authoring pickers. OpenFeature /
 * Flagsmith, so honour the local-dev force-enable escape hatch.
 *
 * `false` is ambiguous: the flag is off, or OpenFeature has not resolved yet
 * (`useBooleanFlagValue` returns this static default while the SDK bootstraps).
 * Callers that persist or one-shot-seed from this value must pair the read
 * with `useWhenProviderReady({ suspendUntilReady: false })` and must not treat
 * an unresolved `false` as authoritative.
 */
export const useIsSessionTranscribingEventsEnabled = () => {
  const enabled = useBooleanFlagValue(
    'release-ml-scribe-session-transcribing-events',
    false
  );
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return enabled;
};
